import { AirlineCreateDTO, AirlinesClient, AirlineUpdateDTO, IAirlineCreateDTO, IAirlineDetailDTO, IAirlineUpdateBaseDTO, IAirlineUpdateDTO } from 'api/swagger/ApiClient';
import { autoinject, computedFrom, newInstance } from 'aurelia-framework';
import { Redirect, RouteConfig, Router } from 'aurelia-router';
import { ValidationController } from 'aurelia-validation';
import { watch } from 'aurelia-watch-decorator';
import { DialogHelper } from 'framework/aurelia_dialogs/dialog-helper';
import { BootstrapGroupRenderer } from 'framework/aurelia_tools/bootstrap-group-renderer';
import { log } from 'framework/script_utilities/dev-decorators';
import { cloneDeep } from 'framework/script_utilities/object-equalizer';
import { EnumsService } from 'services/enums-service';
import { requireServices } from 'support/activation';
import { ToastFormat, ToastHelper, ToastType } from 'support/toaster';
import { ValidationHelper } from 'support/validation';

@autoinject
export class AdminAirlineDetailScreen {
  id: number = null;
  route: RouteConfig = null;
  model: IAirlineDetailDTO = null;
  backup: IAirlineDetailDTO = null;
  loaded = false;
  changed = false;

  constructor(private enums: EnumsService, private api: AirlinesClient, private router: Router, private dialogHelper: DialogHelper, private toastHelper: ToastHelper, private validationHelper: ValidationHelper, @newInstance() private validationController: ValidationController, validationRenderer: BootstrapGroupRenderer) {
    validationRenderer.allowedParents = 'bs-form-input, bs-form-lookup, bs-form-select, bs-form-textarea';
    this.validationController.addRenderer(validationRenderer);
  }

  async canActivate() {
    await requireServices(this.enums);
  }

  @log('debug')
  async activate(params, route: RouteConfig) {
    this.id = parseInt(params?.id) || null;
    this.route = route;
  }

  bind() {
    this.initialize();
  }

  private async initialize() {
    this.bindDetail(this.id ? await this.api.getById(this.id) : null);
    this.loaded = true;
  }

  async canDeactivate() {
    if (this.editing && this.changed && !await this.dialogHelper.confirm({ dialogTitle: 'Leave without saving?', message: 'You haven\'t saved the form yet. If you proceed, your changes will be lost!', okTitle: 'Leave without saving', cancelTitle: 'Stay here!' }))
      return new Redirect((this.router.history as any).previousLocation, { trigger: false, replace: false });
  }

  @log('debug')
  private bindDetail(dto?: IAirlineDetailDTO) {
    this.id = dto?.id ?? null;
    if (dto == null)
      this.model = {
        name: null,
        code: null,
        airlineRating: null,
        airlineStatus: { name: null, value: null },
        hasDeviation: false,
        comment: null
      } as IAirlineDetailDTO;
    else
      this.model = dto;
    this.backup = cloneDeep(this.model);
  }

  archiveButtonVisible = false;
  restoreButtonVisible = false;
  submitButtonVisible = false;
  cancelButtonVisible = false;

  @watch((x: AdminAirlineDetailScreen) => x.model)
  @watch((x: AdminAirlineDetailScreen) => x.editing)
  @watch((x: AdminAirlineDetailScreen) => x.changed)
  private updateButtons() {
    this.archiveButtonVisible = this.model.isActive && this.id != null;
    this.restoreButtonVisible = !this.model.isActive && this.id != null;
    this.submitButtonVisible = this.editing && this.changed;
    this.cancelButtonVisible = this.editing && this.changed;
  }

  @computedFrom('id', 'model')
  get editing() {
    return this.id == null || this.model?.isActive;
  }

  async submit() {
    await this.validationHelper.validate(this.validationController) && await this.save();
  }

  async cancel() {
    if (!this.changed || await this.dialogHelper.confirm({ dialogTitle: 'Cancel your changes?', message: 'You haven\'t saved the form yet. If you proceed, your changes will be lost!', okTitle: 'Sure, do it!', cancelTitle: 'Back to form' })) {
      this.changed && this.bindDetail(this.backup);
    }
  }

  async archive() {
    this.bindDetail(await this.api.archiveAirline(this.id));
    this.toastHelper.toast('Airline has been successfully <b>archived</b>.', 'Archived', ToastType.Success, ToastFormat.Html);
  }

  async restore() {
    this.bindDetail(await this.api.unarchiveAirline(this.id));
    this.toastHelper.toast('Airline has been successfully <b>restored</b>.', 'Restored', ToastType.Success, ToastFormat.Html);
  }

  private async save() {
    const data = this.getSaveData(this.model);
    let dto = null as IAirlineDetailDTO;
    if (this.id)
      dto = await this.api.updateAirline(this.id, new AirlineUpdateDTO(data as IAirlineUpdateDTO));
    else
      dto = await this.api.createAirline(new AirlineCreateDTO(data as IAirlineCreateDTO))
    this.bindDetail(dto);
    this.toastHelper.toast('Airline has been successfully <b>saved</b>.', 'Saved', ToastType.Success, ToastFormat.Html);
  }

  getSaveData(model: IAirlineDetailDTO): IAirlineUpdateBaseDTO {
    return {
      name: model.name,
      code: model.code,
      airlineRating: model.airlineRating.value,
      comment: model.comment
    }
  }

  @computedFrom('id', 'model', 'model.name')
  get title() {
    return this.id == null && this.model?.name == null ? 'New Airline' : (this.model != null ? this.model.name : '');
  }

  @watch((x: AdminAirlineDetailScreen) => x.model)
  private modelChanged() {
    this.route.navModel.setTitle(this.title);
    this.validationController.reset();
  }
}
